import { useSelector } from 'react-redux';
import { hasCustomConfiguration, isRecurringCheckoutLineItem, isOptional, isFutureStartDateCheckoutLineItem, isRelativeStartDateCheckoutLineItem } from '../types/utils/typeguards';
import { getBuyerOverridesIsLoading, getExpiredFutureStartDateLineItemIds } from '../state/BuyerOverrides/selectors';
import { useValidationErrors } from './useValidationErrors';
import useMinMaxAmountsFromValidationErrors from './useMinMaxAmountsFromValidationErrors';
import { MAX_TOTAL_EXCEEDED, MIN_TOTAL_NOT_REACHED } from '../constants/ValidationErrors';
const calculateLineItemValidation = (lineItem, minAmount, maxAmount, validationErrors) => {
  if (isRecurringCheckoutLineItem(lineItem)) {
    const hasMinViolation = minAmount ? !lineItem.amount || lineItem.amount < minAmount : false;
    const hasMaxViolation = maxAmount ? lineItem.amount >= maxAmount : false;
    return hasMinViolation || hasMaxViolation;
  } else {
    // Show error on at-checkout items when total violates limits
    return validationErrors.some(error => error.subCategory === MIN_TOTAL_NOT_REACHED || error.subCategory === MAX_TOTAL_EXCEEDED);
  }
};

/**
 * This hook returns an array of validation states for configurable line items.
 * eg. for an Order summary of 2 lineItems it returns [
 *   {hasValidationError: false, hasFutureValidationError: false, hasFutureItemExpired: false},
 *   {hasValidationError: true, hasFutureValidationError: false, hasFutureItemExpired: false}
 * ]
 */
export const useLineItemErrors = ({
  lineItems,
  blurStates,
  showAllErrors,
  readOnly,
  selectedItems
}) => {
  const loadingOverrides = useSelector(getBuyerOverridesIsLoading);
  const {
    subtotalsValidationErrors
  } = useValidationErrors();
  const atCheckoutValidationErrors = subtotalsValidationErrors.filter(error => {
    var _error$context;
    return !((_error$context = error.context) !== null && _error$context !== void 0 && (_error$context = _error$context.initialPaymentType) !== null && _error$context !== void 0 && _error$context.includes('FUTURE_START_DATE'));
  });
  const futureValidationErrors = subtotalsValidationErrors.filter(error => {
    var _error$context2;
    return !!((_error$context2 = error.context) !== null && _error$context2 !== void 0 && (_error$context2 = _error$context2.initialPaymentType) !== null && _error$context2 !== void 0 && _error$context2.includes('FUTURE_START_DATE'));
  });
  const {
    minimumAmount: normalMinAmount,
    maximumAmount: normalMaxAmount
  } = useMinMaxAmountsFromValidationErrors({
    validationErrors: atCheckoutValidationErrors,
    shouldShowAtCheckout: true,
    shouldShowRecurring: true
  });
  const {
    minimumAmount: futureMinAmount,
    maximumAmount: futureMaxAmount
  } = useMinMaxAmountsFromValidationErrors({
    validationErrors: futureValidationErrors,
    shouldShowAtCheckout: false,
    shouldShowRecurring: true
  });
  const blurStatesArray = Object.values(blurStates);
  const areEditableItemsBlurred = blurStatesArray.length > 0 && blurStatesArray.every(v => v === true) || showAllErrors;

  // For optional products - only one item becomes blurred on checkout click, so we see if any items are blurred
  const areSomeEditableItemsBlurred = blurStatesArray.length > 0 && blurStatesArray.some(v => v === true);
  const areOptionalItemsSelected = selectedItems.some(i => i === true);
  const areAllItemsOptional = lineItems.every(l => hasCustomConfiguration(l) && isOptional(l));
  const areAllLineItemsOptionalUnselected = areAllItemsOptional && !areOptionalItemsSelected;
  const areEditableRecurringItemsPresent = lineItems.some(l => hasCustomConfiguration(l) && isRecurringCheckoutLineItem(l));
  const expiredFutureStartDateLineItemIds = new Set(useSelector(getExpiredFutureStartDateLineItemIds));
  const areEditableRecurringItemsBlurred = areEditableRecurringItemsPresent &&
  // Casting to any due to typescript issue https://github.com/microsoft/TypeScript/issues/44373
  lineItems.every(lineItem => !hasCustomConfiguration(lineItem) || !isRecurringCheckoutLineItem(lineItem) || blurStates[lineItem.id] === true);
  const areRequiredItemsBlurred = !areEditableItemsBlurred && !areEditableRecurringItemsBlurred && expiredFutureStartDateLineItemIds.size === 0;
  const hideOptionalItemValidation = !areSomeEditableItemsBlurred && !areOptionalItemsSelected;
  const hideValidation = areAllItemsOptional ? hideOptionalItemValidation : areRequiredItemsBlurred;
  if (loadingOverrides || readOnly || hideValidation) {
    return lineItems.map(() => ({
      hasValidationError: false,
      hasFutureValidationError: false,
      hasFutureItemExpired: false
    }));
  }
  const result = lineItems.map((lineItem, index) => {
    // skips validation for optional items that are not selected
    if (hasCustomConfiguration(lineItem) && isOptional(lineItem) && !selectedItems[index]) {
      return {
        hasValidationError: areAllLineItemsOptionalUnselected,
        hasFutureValidationError: areAllLineItemsOptionalUnselected,
        hasFutureItemExpired: false
      };
    }
    const isFutureLineItem = isFutureStartDateCheckoutLineItem(lineItem) || isRelativeStartDateCheckoutLineItem(lineItem);
    const shouldShowErrorsForItem = showAllErrors || blurStates[lineItem.id];
    let hasValidationError = false;
    let hasFutureValidationError = false;
    if (shouldShowErrorsForItem) {
      if (!isFutureLineItem && atCheckoutValidationErrors.length > 0) {
        hasValidationError = calculateLineItemValidation(lineItem, normalMinAmount, normalMaxAmount, atCheckoutValidationErrors);
      }
      if (isFutureLineItem && futureValidationErrors.length > 0) {
        hasFutureValidationError = calculateLineItemValidation(lineItem, futureMinAmount, futureMaxAmount, futureValidationErrors);
      }
    }
    const hasFutureItemExpired = expiredFutureStartDateLineItemIds.has(lineItem.id);
    return {
      hasValidationError,
      hasFutureValidationError,
      hasFutureItemExpired
    };
  });
  return result;
};